import React, { useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Lenis from "lenis";
import "./App.css";

gsap.registerPlugin(ScrollTrigger);

const BOOK_URL = "https://calendar.notion.so/meet/alex-yelisieiev/book";
const CHAT_URL =
  "https://go.crisp.chat/chat/embed/?website_id=1f81e74b-b9f2-4909-a79a-84e0fe74b081";

const expertise = [
  {
    title: "AI Infrastructure",
    body: "End-to-end design and pipeline deployment – models, orchestration, and the rails they run on.",
  },
  {
    title: "Backend Engineering",
    body: "Server-side architecture that ships as a ready system, not a prototype glued to an API.",
  },
  {
    title: "System Audits",
    body: "Forensic review of existing codebases for misuse of AI, performance drag, and brittle edges.",
  },
  {
    title: "Orchestration",
    body: "Models are getting smarter. Putting them on the right track still matters as much as the architecture: prompting, context management, and chaining.",
  },
];

const approachBeats = [
  {
    index: "01",
    title: "API glue",
    body: "Wire a model, ship a happy path, call it done. It works in the demo – then collapses on edge cases.",
  },
  {
    index: "02",
    title: "Predictable logic",
    body: "We engineer flows that stay coherent under load, ambiguity, and the weird inputs production always finds.",
  },
  {
    index: "03",
    title: "Correct AI",
    body: "Models used at capacity. Systems composed for multimodal reach. Architecture that opens horizons – not shortcuts with a latency bill.",
  },
];

const testimonials = [
  {
    quote:
      "We have a portfolio of SaaS tools, and worked with Alex for a very productive couple of months of automating processes. He was interested in, and quickly understood, the nuances involved on the people and biz fronts, and made automations that are still running and driving biz value to this day. Wholeheartedly recommend working with Alex & Kinomi.",
    name: "Thomas James",
    url: "https://x.com/thomasjamesio",
    handle: "thomasjamesio",
    photo: "/thomas_james.jpg",
    photoWidth: 400,
    photoHeight: 400,
  },
];

/** Official X mark path from x.com (brand toolkit: white on dark, black on light). */
const XLogo: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path
      fill="currentColor"
      d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.74l7.995-9.142L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"
    />
  </svg>
);

const App: React.FC = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isBooting, setIsBooting] = useState(true);
  const rootRef = useRef<HTMLDivElement>(null);
  const bootRef = useRef<HTMLDivElement>(null);
  const lenisRef = useRef<Lenis | null>(null);

  const scrollToSection = (id: string) => {
    // Closing must paint before scroll: `.is-menu-open` collapses the page to
    // 100svh, so Lenis/native scrollTo has nowhere to go while the menu is open.
    flushSync(() => {
      setIsMobileMenuOpen(false);
    });

    const element = document.getElementById(id);
    const lenis = lenisRef.current;
    lenis?.start();

    if (lenis) {
      lenis.scrollTo(element ?? 0, { offset: 0, duration: 1.05, force: true });
      return;
    }

    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    } else if (id === "top") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  useEffect(() => {
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    // Lenis + ScrollTrigger is unreliable on iOS Safari (incl. betas): use native scroll.
    const preferNativeScroll =
      window.matchMedia("(hover: none), (pointer: coarse)").matches ||
      (typeof navigator !== "undefined" && navigator.maxTouchPoints > 1);
    const root = rootRef.current;
    const boot = bootRef.current;
    if (!root) return;

    let cancelled = false;
    let bootFinished = false;
    let bootSafety = 0;
    let lenis: Lenis | null = null;
    const tick = (time: number) => {
      lenis?.raf(time * 1000);
    };

    document.documentElement.classList.add("is-booting");

    ScrollTrigger.config({
      ignoreMobileResize: true,
      limitCallbacks: true,
      autoRefreshEvents: "visibilitychange,DOMContentLoaded,load",
    });

    if (!reduceMotion && !preferNativeScroll) {
      lenis = new Lenis({
        duration: 0.9,
        easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
        smoothWheel: true,
        syncTouch: false,
        touchMultiplier: 1.1,
        wheelMultiplier: 0.95,
        autoRaf: false,
      });

      lenisRef.current = lenis;
      lenis.on("scroll", ScrollTrigger.update);
      gsap.ticker.add(tick);
      gsap.ticker.lagSmoothing(0);
    }

    const ctx = gsap.context(() => {
      gsap.set(
        [
          ".nav-brand",
          ".nav-links li",
          ".nav-toggle",
          ".hero-kicker",
          ".hero-headline",
          ".hero-lede",
          ".hero-cta",
          ".hero-scroll",
          ".lattice-line",
          ".lattice-node",
        ],
        { opacity: 0 }
      );
      gsap.set(".hero-brand .reveal-line > span", { yPercent: 110 });

      const percentEl = boot?.querySelector<HTMLElement>(".boot-percent-value");
      const progress = { value: 0 };

      if (boot && !reduceMotion) {
        gsap.set(".boot-line > span", { yPercent: 110 });
        gsap.set(".boot-label, .boot-percent", { opacity: 0, y: 12 });
        gsap.set(".boot-fill", { scaleX: 0, transformOrigin: "left center" });
        gsap.set(".boot-glow", { opacity: 0, scale: 0.92 });

        gsap
          .timeline({ defaults: { ease: "expo.out" } })
          .to(".boot-glow", { opacity: 1, scale: 1, duration: 1.1 }, 0)
          .to(".boot-label, .boot-percent", { opacity: 1, y: 0, duration: 0.7, stagger: 0.06 }, 0.08)
          .to(
            ".boot-line > span",
            { yPercent: 0, duration: 1.05, stagger: 0.1 },
            0.12
          );
      }

      const playIntro = () => {
        const load = gsap.timeline({ defaults: { ease: "expo.out" } });

        load
          .fromTo(
            ".nav-brand, .nav-links li, .nav-toggle",
            { y: -18, opacity: 0 },
            {
              y: 0,
              opacity: 1,
              duration: 0.7,
              stagger: 0.05,
            }
          )
          .fromTo(
            ".hero-brand .reveal-line > span",
            { yPercent: 110 },
            {
              yPercent: 0,
              duration: 1.2,
              stagger: 0.09,
              opacity: 1,
            },
            "-=0.35"
          )
          .fromTo(
          ".hero-kicker, .hero-headline, .hero-lede, .hero-cta, .hero-scroll",
          { y: 36, opacity: 0 },
          {
              y: 0,
              opacity: 1,
              duration: 0.85,
              stagger: 0.08,
            },
            "-=0.55"
          )
          .fromTo(
            ".lattice-line, .lattice-node",
            { opacity: 0 },
            {
              opacity: 1,
              duration: 1.1,
              stagger: { each: 0.014, from: "random" },
              ease: "power2.out",
            },
            0.15
          );
      };

      const runBootProgress = () =>
        new Promise<void>((resolve) => {
          if (!boot || reduceMotion) {
            if (percentEl) percentEl.textContent = "100";
            resolve();
            return;
          }

          gsap.to(progress, {
            value: 100,
            duration: 1.35,
            ease: "power2.inOut",
            onUpdate: () => {
              if (percentEl) {
                percentEl.textContent = String(Math.round(progress.value)).padStart(2, "0");
              }
            },
          });

          gsap.to(".boot-fill", {
            scaleX: 1,
            duration: 1.35,
            ease: "power2.inOut",
            onComplete: resolve,
          });
        });

      const exitBoot = () =>
        new Promise<void>((resolve) => {
          if (!boot || reduceMotion) {
            resolve();
            return;
          }

          const exit = gsap.timeline({
            defaults: { ease: "expo.inOut" },
            onComplete: resolve,
          });

          exit
            .to(".boot-label, .boot-percent", { opacity: 0, y: -10, duration: 0.35 }, 0)
            .to(
              ".boot-line > span",
              { yPercent: -110, duration: 0.55, stagger: 0.05 },
              0.05
            )
            .to(".boot-fill", { scaleX: 0, transformOrigin: "right center", duration: 0.4 }, 0.05)
            .to(".boot-glow", { opacity: 0, duration: 0.45 }, 0.1)
            .to(
              boot,
              {
                yPercent: -100,
                duration: 0.9,
                ease: "expo.inOut",
              },
              0.2
            );
        });

      const finishBoot = () => {
        if (cancelled || bootFinished) return;
        bootFinished = true;
        document.documentElement.classList.remove("is-booting");
        setIsBooting(false);
        playIntro();
        requestAnimationFrame(() => {
          ScrollTrigger.refresh();
        });
      };

      const ready = async () => {
        const started = performance.now();
        const progressDone = runBootProgress();
        const fontsReady = document.fonts?.ready ?? Promise.resolve();
        try {
          await Promise.race([
            fontsReady,
            new Promise<void>((r) => window.setTimeout(r, 700)),
          ]);
        } catch {
          /* ignore */
        }
        const remaining = Math.max(0, 1400 - (performance.now() - started));
        await Promise.all([
          progressDone,
          new Promise<void>((r) => window.setTimeout(r, remaining)),
        ]);
        if (cancelled || bootFinished) return;

        await exitBoot();
        if (cancelled || bootFinished) return;

        finishBoot();
      };

      // Hard safety: never leave hero/nav stuck at opacity 0 on flaky mobile Safari.
      bootSafety = window.setTimeout(() => {
        if (cancelled || bootFinished) return;
        gsap.killTweensOf(boot);
        gsap.set(boot, { clearProps: "transform" });
        finishBoot();
      }, 4200);

      void ready()
        .catch(() => {
          finishBoot();
        })
        .finally(() => {
          window.clearTimeout(bootSafety);
        });

      if (!reduceMotion) {
        const signalPath = root.querySelector<SVGPathElement>(".signal-path");
        ScrollTrigger.create({
          trigger: ".hero",
          start: "top bottom",
          end: "bottom top",
          onToggle: (self) => {
            signalPath?.classList.toggle("is-idle", !self.isActive);
          },
        });

        // Scrubbed parallax is a common stutter source on iOS Safari.
        if (!preferNativeScroll) {
          gsap.to(".hero-lattice", {
            yPercent: 6,
            force3D: true,
            ease: "none",
            scrollTrigger: {
              trigger: ".hero",
              start: "top top",
              end: "bottom top",
              scrub: 0.35,
            },
          });
        }

        const beats = gsap.utils.toArray<HTMLElement>(".approach-beat");
        if (beats.length > 1 && window.matchMedia("(min-width: 900px)").matches) {
          // Inline styles win over CSS defaults; keep inactive beats fully hidden
          // (Brave / reduced-motion-adjacent paths were leaving absolute beats stacked).
          gsap.set(beats, { autoAlpha: 0, y: 40, force3D: true });
          gsap.set(beats[0], { autoAlpha: 1, y: 0 });

          const pinTl = gsap.timeline({
            scrollTrigger: {
              trigger: ".approach-pin",
              start: "top top",
              end: "+=230%",
              pin: true,
              scrub: 0.35,
              anticipatePin: 0.5,
              invalidateOnRefresh: true,
              fastScrollEnd: true,
            },
          });

          beats.forEach((beat, i) => {
            if (i === 0) return;
            pinTl
              .to(beats[i - 1], { autoAlpha: 0, y: -28, duration: 0.5 }, i)
              .fromTo(
                beat,
                { autoAlpha: 0, y: 40 },
                { autoAlpha: 1, y: 0, duration: 0.5, immediateRender: false },
                i
              );
          });
        }
      }

      gsap.utils.toArray<HTMLElement>(".expertise-item").forEach((item, i) => {
        gsap.from(item, {
          y: 48,
          opacity: 0,
          duration: 0.9,
          delay: i * 0.04,
          ease: "expo.out",
          force3D: true,
          immediateRender: false,
          scrollTrigger: {
            trigger: item,
            start: "top 88%",
            once: true,
            invalidateOnRefresh: true,
          },
        });
      });

      gsap.utils.toArray<HTMLElement>(".section-canopy-layer").forEach((layer) => {
        gsap.from(layer, {
          opacity: 0,
          duration: 1.2,
          ease: "power2.out",
          immediateRender: false,
          scrollTrigger: {
            trigger: layer,
            start: "top 80%",
            once: true,
            invalidateOnRefresh: true,
          },
        });
      });

      gsap.from(".testimonials .section-label, .testimonial-quote, .testimonial-person", {
        y: 40,
        opacity: 0,
        duration: 0.95,
        stagger: 0.1,
        ease: "expo.out",
        force3D: true,
        immediateRender: false,
        scrollTrigger: {
          trigger: ".testimonials",
          start: "top 78%",
          once: true,
          invalidateOnRefresh: true,
        },
      });

      gsap.from(".founder .section-label, .founder-photo, .founder-copy > *", {
        y: 40,
        opacity: 0,
        duration: 0.95,
        stagger: 0.1,
        ease: "expo.out",
        force3D: true,
        immediateRender: false,
        scrollTrigger: {
          trigger: ".founder",
          start: "top 78%",
          once: true,
          invalidateOnRefresh: true,
        },
      });

      const waveEl = root.querySelector<HTMLElement>(".founder-wave");
      const playWave = () => {
        if (!waveEl || reduceMotion || gsap.isTweening(waveEl)) return;
        gsap
          .timeline({ defaults: { transformOrigin: "75% 85%", force3D: true } })
          .to(waveEl, { rotation: 16, duration: 0.16, ease: "power2.out" })
          .to(waveEl, { rotation: -8, duration: 0.14, ease: "power2.inOut" })
          .to(waveEl, { rotation: 14, duration: 0.14, ease: "power2.inOut" })
          .to(waveEl, { rotation: -4, duration: 0.12, ease: "power2.inOut" })
          .to(waveEl, { rotation: 0, duration: 0.22, ease: "power3.out" });
      };

      if (waveEl && !reduceMotion) {
        ScrollTrigger.create({
          trigger: ".founder",
          start: "top 72%",
          once: true,
          onEnter: () => {
            gsap.delayedCall(0.9, playWave);
          },
        });
      }

      gsap.from(".contact-link, .contact-secondary, .section-title.contact-title", {
        y: 50,
        opacity: 0,
        duration: 1,
        stagger: 0.1,
        ease: "expo.out",
        force3D: true,
        immediateRender: false,
        scrollTrigger: {
          trigger: ".contact",
          start: "top 75%",
          once: true,
          invalidateOnRefresh: true,
        },
      });

      const floatActions = root.querySelector<HTMLElement>(".float-actions");
      const nav = root.querySelector<HTMLElement>(".nav");
      const lightSections = [".expertise", ".founder"];

      const isLightAt = (y: number) =>
        lightSections.some((sel) => {
          const el = root.querySelector(sel);
          if (!el) return false;
          const rect = el.getBoundingClientRect();
          return rect.top < y && rect.bottom > y;
        });

      const syncLightChrome = () => {
        nav?.classList.toggle("is-on-light", isLightAt(72));
        floatActions?.classList.toggle(
          "is-on-light",
          isLightAt(window.innerHeight - 72)
        );
      };

      lightSections.forEach((trigger) => {
        ScrollTrigger.create({
          trigger,
          start: "top bottom",
          end: "bottom top",
          onEnter: syncLightChrome,
          onLeave: syncLightChrome,
          onEnterBack: syncLightChrome,
          onLeaveBack: syncLightChrome,
          onUpdate: syncLightChrome,
        });
      });

      const cleanups: Array<() => void> = [];
      const canHover = window.matchMedia("(hover: hover) and (pointer: fine)").matches;

      if (canHover) {
        root.querySelectorAll<HTMLElement>(".magnetic").forEach((el) => {
          const xTo = gsap.quickTo(el, "x", { duration: 0.4, ease: "power3.out" });
          const yTo = gsap.quickTo(el, "y", { duration: 0.4, ease: "power3.out" });
          const onMove = (e: MouseEvent) => {
            const rect = el.getBoundingClientRect();
            xTo((e.clientX - rect.left - rect.width / 2) * 0.22);
            yTo((e.clientY - rect.top - rect.height / 2) * 0.22);
          };
          const onLeave = () => {
            xTo(0);
            yTo(0);
          };
          el.addEventListener("mousemove", onMove, { passive: true });
          el.addEventListener("mouseleave", onLeave);
          cleanups.push(() => {
            el.removeEventListener("mousemove", onMove);
            el.removeEventListener("mouseleave", onLeave);
          });
        });
      }

      return () => {
        cleanups.forEach((fn) => fn());
      };
    }, root);

    return () => {
      cancelled = true;
      window.clearTimeout(bootSafety);
      document.documentElement.classList.remove("is-booting");
      ctx.revert();
      gsap.ticker.remove(tick);
      gsap.ticker.lagSmoothing(500, 33);
      lenisRef.current = null;
      lenis?.destroy();
      ScrollTrigger.getAll().forEach((t) => t.kill());
    };
  }, []);

  useEffect(() => {
    const lenis = lenisRef.current;
    const locked = isBooting || isMobileMenuOpen;

    document.body.style.overflow = locked ? "hidden" : "";

    if (isMobileMenuOpen) {
      lenis?.stop();
    } else if (!isBooting) {
      lenis?.start();
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [isBooting, isMobileMenuOpen]);

  return (
    <div
      ref={rootRef}
      className={`site${isMobileMenuOpen ? " is-menu-open" : ""}`}
    >
      {isBooting && (
        <div ref={bootRef} className="boot" aria-hidden="true">
          <div className="boot-glow" />
          <div className="boot-frame">
            <p className="boot-percent">
              <span className="boot-percent-value">00</span>
            </p>
            <div className="boot-inner">
              <p className="boot-label">Loading</p>
              <h1 className="boot-mark">
                <span className="boot-line">
                  <span>kinomi</span>
                </span>
                <span className="boot-line">
                  <span>
                    <span className="boot-dot">.</span>tech
                  </span>
                </span>
              </h1>
              <div className="boot-track">
                <div className="boot-fill" />
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="atmosphere" aria-hidden="true" />

      <svg width="0" height="0" style={{ position: "absolute" }} aria-hidden="true">
        <defs>
          <clipPath id="canopy-clip-approach" clipPathUnits="objectBoundingBox">
            <path d="M0.69912 0.11331 C0.70139 0.13108 0.70117 0.15722 0.69155 0.17277 C0.68193 0.18833 0.65849 0.20145 0.64139 0.20663 C0.62430 0.21182 0.60546 0.21176 0.58899 0.20389 C0.57251 0.19602 0.55058 0.17441 0.54254 0.15942 C0.53449 0.14442 0.54292 0.12921 0.54071 0.11392 C0.53851 0.09863 0.52111 0.08293 0.52929 0.06769 C0.53747 0.05244 0.57251 0.02737 0.58978 0.02246 C0.60706 0.01754 0.61825 0.03091 0.63294 0.03819 C0.64762 0.04547 0.66687 0.05362 0.67790 0.06614 C0.68893 0.07866 0.69684 0.09554 0.69912 0.11331 Z M0.35211 0.25458 C0.35068 0.26262 0.34021 0.27499 0.33385 0.28103 C0.32750 0.28706 0.32265 0.28700 0.31400 0.29078 C0.30534 0.29456 0.29073 0.30451 0.28194 0.30370 C0.27315 0.30288 0.26532 0.29364 0.26128 0.28589 C0.25723 0.27815 0.25563 0.26641 0.25767 0.25725 C0.25972 0.24809 0.26788 0.23678 0.27354 0.23095 C0.27919 0.22511 0.28367 0.22649 0.29160 0.22224 C0.29952 0.21799 0.31263 0.20367 0.32111 0.20543 C0.32959 0.20720 0.33731 0.22461 0.34248 0.23280 C0.34764 0.24099 0.35355 0.24654 0.35211 0.25458 Z M0.76053 0.64859 C0.76095 0.66135 0.75812 0.68015 0.75199 0.69008 C0.74585 0.70000 0.73570 0.70124 0.72372 0.70814 C0.71174 0.71504 0.69247 0.73400 0.68010 0.73149 C0.66772 0.72898 0.65544 0.70693 0.64949 0.69307 C0.64354 0.67922 0.64482 0.66146 0.64442 0.64836 C0.64402 0.63526 0.64076 0.62467 0.64711 0.61446 C0.65346 0.60425 0.66973 0.59113 0.68250 0.58710 C0.69526 0.58306 0.71255 0.58583 0.72370 0.59024 C0.73486 0.59464 0.74332 0.60380 0.74945 0.61353 C0.75559 0.62325 0.76011 0.63583 0.76053 0.64859 Z M0.88652 0.13082 C0.88681 0.13728 0.89206 0.14345 0.88822 0.15015 C0.88438 0.15686 0.87162 0.16833 0.86347 0.17107 C0.85531 0.17380 0.84610 0.17092 0.83929 0.16657 C0.83249 0.16223 0.82562 0.15120 0.82264 0.14498 C0.81965 0.13877 0.82180 0.13656 0.82137 0.12929 C0.82094 0.12202 0.81759 0.10892 0.82006 0.10137 C0.82253 0.09381 0.82770 0.08552 0.83621 0.08395 C0.84473 0.08239 0.86277 0.08743 0.87114 0.09200 C0.87951 0.09658 0.88387 0.10493 0.88643 0.11141 C0.88900 0.11788 0.88622 0.12437 0.88652 0.13082 Z M0.33948 0.49919 C0.33725 0.51568 0.32961 0.53369 0.31986 0.54620 C0.31012 0.55872 0.29400 0.57073 0.28102 0.57429 C0.26804 0.57785 0.25199 0.57396 0.24196 0.56756 C0.23193 0.56117 0.22937 0.54559 0.22085 0.53592 C0.21234 0.52624 0.19322 0.52225 0.19086 0.50951 C0.18851 0.49677 0.19711 0.46838 0.20672 0.45946 C0.21633 0.45055 0.23417 0.45816 0.24853 0.45604 C0.26288 0.45392 0.27873 0.44822 0.29285 0.44676 C0.30697 0.44530 0.32549 0.43853 0.33326 0.44727 C0.34103 0.45601 0.34171 0.48271 0.33948 0.49919 Z" />
          </clipPath>
        </defs>
      </svg>

      <div className="float-actions">
        <a
          href={CHAT_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="float-btn magnetic"
          aria-label="Chat with us"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
            <path d="M5 7.5A2.5 2.5 0 017.5 5h9A2.5 2.5 0 0119 7.5v6a2.5 2.5 0 01-2.5 2.5H11l-4 3v-3H7.5A2.5 2.5 0 015 13.5v-6z" />
          </svg>
        </a>
        <a
          href={BOOK_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="float-btn magnetic"
          aria-label="Book a call"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
            <rect x="4.5" y="5.5" width="15" height="14" rx="2" />
            <path d="M4.5 10h15M9 3.5v4M15 3.5v4" />
          </svg>
        </a>
      </div>

      <div className="site-content">
        <nav className="nav" aria-label="Primary">
          <a
            href="#top"
            className="nav-brand"
            onClick={(e) => {
              e.preventDefault();
              scrollToSection("top");
            }}
          >
            kinomi.tech
          </a>

          <ul className="nav-links">
            <li>
              <button type="button" onClick={() => scrollToSection("expertise")}>
                Expertise
              </button>
            </li>
            <li>
              <button type="button" onClick={() => scrollToSection("approach")}>
                Approach
              </button>
            </li>
            <li>
              <button type="button" onClick={() => scrollToSection("testimonials")}>
                Testimonials
              </button>
            </li>
            <li>
              <button type="button" onClick={() => scrollToSection("founder")}>
                Founder
              </button>
            </li>
            <li>
              <button type="button" onClick={() => scrollToSection("contact")}>
                Contact
              </button>
            </li>
          </ul>

          <button
            type="button"
            className="nav-toggle"
            onClick={() => setIsMobileMenuOpen((open) => !open)}
            aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={isMobileMenuOpen}
          >
            {isMobileMenuOpen ? (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M6 6l12 12M18 6L6 18" />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M4 7h16M4 12h16M4 17h16" />
              </svg>
            )}
          </button>
        </nav>

        <div
          className={`mobile-menu${isMobileMenuOpen ? " is-open" : ""}`}
          onClick={() => setIsMobileMenuOpen(false)}
        >
          <div className="mobile-menu-links" onClick={(e) => e.stopPropagation()}>
            <button type="button" onClick={() => scrollToSection("expertise")}>
              Expertise
            </button>
            <button type="button" onClick={() => scrollToSection("approach")}>
              Approach
            </button>
            <button type="button" onClick={() => scrollToSection("testimonials")}>
              Testimonials
            </button>
            <button type="button" onClick={() => scrollToSection("founder")}>
              Founder
            </button>
            <button type="button" onClick={() => scrollToSection("contact")}>
              Contact
            </button>
          </div>
        </div>

        <header className="hero" id="top">
          <svg width="0" height="0" style={{ position: "absolute" }} aria-hidden="true">
            <defs>
              <clipPath id="canopy-clip-bg" clipPathUnits="objectBoundingBox">
                <path
                  d="M0.44229 0.39020 C0.44146 0.41093 0.42341 0.43541 0.41279 0.45184 C0.40216 0.46827 0.39273 0.47929 0.37854 0.48878 C0.36435 0.49826 0.34486 0.51150 0.32765 0.50875 C0.31043 0.50599 0.28329 0.49200 0.27523 0.47224 C0.26717 0.45248 0.27827 0.41604 0.27930 0.39020 C0.28032 0.36436 0.27279 0.33551 0.28139 0.31720 C0.28999 0.29890 0.31442 0.28732 0.33088 0.28036 C0.34734 0.27340 0.36567 0.26758 0.38015 0.27543 C0.39463 0.28328 0.40739 0.30831 0.41775 0.32744 C0.42811 0.34657 0.44312 0.36946 0.44229 0.39020 Z M0.30751 0.10171 C0.30758 0.11891 0.29710 0.14268 0.28698 0.15431 C0.27686 0.16595 0.26014 0.17410 0.24681 0.17151 C0.23348 0.16892 0.21081 0.15446 0.20699 0.13880 C0.20317 0.12313 0.21699 0.09364 0.22389 0.07752 C0.23079 0.06139 0.23794 0.04646 0.24838 0.04206 C0.25882 0.03767 0.27667 0.04119 0.28653 0.05113 C0.29638 0.06107 0.30743 0.08452 0.30751 0.10171 Z M0.95999 0.13565 C0.95790 0.14828 0.95698 0.15230 0.95246 0.16264 C0.94794 0.17298 0.94133 0.19462 0.93285 0.19771 C0.92437 0.20081 0.90545 0.19157 0.90158 0.18123 C0.89771 0.17088 0.90889 0.14993 0.90964 0.13565 C0.91039 0.12137 0.90223 0.10385 0.90610 0.09555 C0.90997 0.08725 0.92304 0.08728 0.93285 0.08583 C0.94267 0.08438 0.96047 0.07856 0.96500 0.08686 C0.96952 0.09516 0.96208 0.12302 0.95999 0.13565 Z M0.34874 0.66349 C0.34977 0.69267 0.34065 0.75227 0.32655 0.76153 C0.31245 0.77080 0.28291 0.73543 0.26412 0.71909 C0.24532 0.70275 0.21546 0.68764 0.21378 0.66349 C0.21209 0.63935 0.23624 0.58705 0.25400 0.57421 C0.27177 0.56138 0.30457 0.57161 0.32035 0.58649 C0.33614 0.60137 0.34771 0.63432 0.34874 0.66349 Z M0.94637 0.40717 C0.94425 0.43293 0.94154 0.45862 0.93084 0.47253 C0.92014 0.48645 0.89929 0.49444 0.88219 0.49065 C0.86509 0.48687 0.83942 0.47327 0.82825 0.44981 C0.81707 0.42635 0.80633 0.37159 0.81513 0.34990 C0.82393 0.32820 0.85964 0.32496 0.88105 0.31964 C0.90246 0.31431 0.93269 0.30337 0.94357 0.31796 C0.95446 0.33255 0.94850 0.38141 0.94637 0.40717 Z M0.72618 0.80215 C0.72474 0.82511 0.70410 0.85315 0.69010 0.86222 C0.67610 0.87129 0.65531 0.86660 0.64220 0.85659 C0.62909 0.84658 0.61274 0.82376 0.61142 0.80215 C0.61010 0.78054 0.61974 0.73989 0.63429 0.72693 C0.64885 0.71397 0.68344 0.71187 0.69875 0.72441 C0.71407 0.73694 0.72762 0.77918 0.72618 0.80215 Z M1.04976 0.88111 C1.04992 0.90051 1.06140 0.93075 1.05487 0.94015 C1.04834 0.94955 1.02379 0.93110 1.01056 0.93748 C0.99733 0.94386 0.98698 0.97849 0.97550 0.97845 C0.96402 0.97842 0.94824 0.95349 0.94170 0.93727 C0.93516 0.92104 0.93207 0.89597 0.93623 0.88111 C0.94040 0.86624 0.96007 0.86476 0.96668 0.84811 C0.97329 0.83145 0.96879 0.78393 0.97588 0.78116 C0.98298 0.77840 0.99627 0.82443 1.00927 0.83153 C1.02227 0.83862 1.04714 0.81546 1.05389 0.82372 C1.06064 0.83198 1.04960 0.86170 1.04976 0.88111 Z M0.49228 0.86075 C0.49048 0.87404 0.48748 0.88625 0.48181 0.89381 C0.47614 0.90137 0.46677 0.90300 0.45826 0.90610 C0.44976 0.90919 0.43985 0.91575 0.43078 0.91238 C0.42171 0.90902 0.40745 0.89816 0.40384 0.88593 C0.40023 0.87370 0.40444 0.85202 0.40910 0.83901 C0.41377 0.82600 0.42362 0.81277 0.43182 0.80786 C0.44002 0.80295 0.44817 0.80852 0.45829 0.80955 C0.46842 0.81058 0.48692 0.80551 0.49258 0.81404 C0.49825 0.82258 0.49407 0.84745 0.49228 0.86075 Z M0.19621 0.40106 C0.19509 0.41266 0.17972 0.42461 0.17176 0.43066 C0.16379 0.43671 0.15705 0.43832 0.14844 0.43738 C0.13982 0.43644 0.12520 0.43513 0.12004 0.42503 C0.11489 0.41492 0.11297 0.38824 0.11752 0.37673 C0.12207 0.36522 0.13719 0.35856 0.14735 0.35595 C0.15750 0.35334 0.17031 0.35355 0.17845 0.36106 C0.18660 0.36858 0.19733 0.38946 0.19621 0.40106 Z M0.79133 0.12855 C0.79054 0.17126 0.77711 0.22312 0.75583 0.24712 C0.73456 0.27111 0.69227 0.27978 0.66370 0.27250 C0.63512 0.26522 0.59529 0.23774 0.58439 0.20345 C0.57348 0.16916 0.58345 0.09523 0.59825 0.06676 C0.61304 0.03828 0.64610 0.04524 0.67316 0.03258 C0.70021 0.01993 0.74088 -0.02517 0.76057 -0.00917 C0.78027 0.00682 0.79212 0.08583 0.79133 0.12855 Z M0.66561 0.57277 C0.66613 0.58380 0.66349 0.60184 0.65782 0.60858 C0.65215 0.61531 0.64220 0.61442 0.63162 0.61318 C0.62103 0.61195 0.59928 0.61224 0.59432 0.60117 C0.58936 0.59010 0.59573 0.55861 0.60184 0.54674 C0.60796 0.53488 0.62219 0.53071 0.63100 0.52999 C0.63982 0.52926 0.64896 0.53527 0.65473 0.54240 C0.66049 0.54953 0.66510 0.56174 0.66561 0.57277 Z M0.30443 0.94838 C0.30405 0.96889 0.30057 0.99416 0.28849 1.00718 C0.27640 1.02020 0.24333 1.03628 0.23190 1.02647 C0.22048 1.01667 0.21787 0.96983 0.21993 0.94838 C0.22199 0.92692 0.23247 0.90844 0.24427 0.89773 C0.25608 0.88702 0.28072 0.87567 0.29075 0.88412 C0.30077 0.89256 0.30480 0.92786 0.30443 0.94838 Z M0.07332 0.84893 C0.07204 0.86658 0.08315 0.88695 0.07840 0.89761 C0.07366 0.90827 0.05729 0.91199 0.04484 0.91287 C0.03238 0.91376 0.01207 0.91359 0.00368 0.90293 C-0.00471 0.89227 -0.00648 0.86678 -0.00549 0.84893 C-0.00450 0.83108 0.00124 0.80651 0.00963 0.79581 C0.01801 0.78511 0.03210 0.78542 0.04484 0.78474 C0.05757 0.78406 0.08131 0.78102 0.08606 0.79172 C0.09080 0.80242 0.07460 0.83128 0.07332 0.84893 Z M0.16645 0.19405 C0.16646 0.20257 0.16006 0.21045 0.15581 0.21969 C0.15155 0.22892 0.14773 0.24613 0.14091 0.24944 C0.13409 0.25274 0.12099 0.24607 0.11488 0.23952 C0.10877 0.23297 0.10473 0.21966 0.10425 0.21015 C0.10378 0.20064 0.10926 0.19073 0.11203 0.18247 C0.11481 0.17420 0.11632 0.16608 0.12092 0.16054 C0.12552 0.15501 0.13383 0.14792 0.13962 0.14926 C0.14542 0.15059 0.15122 0.16108 0.15570 0.16854 C0.16017 0.17601 0.16643 0.18552 0.16645 0.19405 Z M0.80035 0.47130 C0.80223 0.49312 0.80756 0.52836 0.79707 0.54457 C0.78658 0.56079 0.75233 0.57540 0.73743 0.56858 C0.72252 0.56177 0.71525 0.52642 0.70765 0.50369 C0.70005 0.48097 0.68600 0.44810 0.69183 0.43225 C0.69767 0.41640 0.72702 0.41170 0.74268 0.40860 C0.75834 0.40549 0.77618 0.40318 0.78579 0.41363 C0.79541 0.42408 0.79847 0.44947 0.80035 0.47130 Z M0.20319 0.71326 C0.20262 0.73332 0.18701 0.75901 0.17386 0.77032 C0.16072 0.78164 0.13332 0.79066 0.12434 0.78115 C0.11536 0.77164 0.11862 0.73278 0.11997 0.71326 C0.12132 0.69375 0.12288 0.67461 0.13243 0.66406 C0.14198 0.65350 0.16548 0.64175 0.17727 0.64995 C0.18906 0.65815 0.20376 0.69320 0.20319 0.71326 Z M0.59116 0.16215 C0.59468 0.19146 0.57214 0.26287 0.55391 0.27345 C0.53567 0.28402 0.49792 0.24413 0.48175 0.22558 C0.46559 0.20703 0.45736 0.18386 0.45691 0.16215 C0.45647 0.14045 0.46642 0.10612 0.47906 0.09537 C0.49170 0.08461 0.51409 0.08648 0.53277 0.09761 C0.55146 0.10875 0.58763 0.13285 0.59116 0.16215 Z M0.87138 0.99887 C0.87051 1.01442 0.85751 1.03253 0.84835 1.04067 C0.83918 1.04881 0.82744 1.04974 0.81637 1.04770 C0.80531 1.04566 0.78739 1.04137 0.78194 1.02844 C0.77649 1.01552 0.77775 0.98175 0.78367 0.97017 C0.78958 0.95858 0.80578 0.96273 0.81743 0.95893 C0.82909 0.95512 0.84462 0.94068 0.85361 0.94734 C0.86260 0.95399 0.87226 0.98331 0.87138 0.99887 Z M0.65028 0.98579 C0.64962 0.99708 0.64151 1.00444 0.63609 1.01593 C0.63067 1.02743 0.62567 1.05125 0.61777 1.05476 C0.60988 1.05826 0.59673 1.04524 0.58873 1.03696 C0.58072 1.02868 0.57322 1.01736 0.56974 1.00509 C0.56626 0.99283 0.56362 0.97268 0.56785 0.96337 C0.57209 0.95406 0.58719 0.95381 0.59516 0.94924 C0.60312 0.94468 0.60817 0.93614 0.61565 0.93596 C0.62312 0.93579 0.63424 0.93988 0.64001 0.94819 C0.64578 0.95649 0.65093 0.97450 0.65028 0.98579 Z M0.10786 0.08019 C0.10757 0.09919 0.09600 0.12887 0.08556 0.13696 C0.07512 0.14505 0.05809 0.13282 0.04523 0.12873 C0.03237 0.12464 0.01280 0.12434 0.00841 0.11241 C0.00402 0.10048 0.01317 0.07387 0.01891 0.05714 C0.02465 0.04042 0.03148 0.01777 0.04287 0.01208 C0.05427 0.00638 0.07644 0.01163 0.08727 0.02298 C0.09810 0.03433 0.10814 0.06120 0.10786 0.08019 Z M0.60039 0.36372 C0.60072 0.37716 0.59765 0.39442 0.59018 0.40692 C0.58271 0.41942 0.56721 0.44034 0.55556 0.43871 C0.54392 0.43709 0.52680 0.41565 0.52031 0.39717 C0.51382 0.37869 0.51033 0.34218 0.51663 0.32784 C0.52293 0.31349 0.54620 0.31137 0.55813 0.31111 C0.57007 0.31084 0.58118 0.31746 0.58822 0.32622 C0.59526 0.33499 0.60007 0.35027 0.60039 0.36372 Z M0.07046 0.41445 C0.07216 0.44083 0.09258 0.49093 0.08491 0.50148 C0.07724 0.51202 0.04265 0.48007 0.02443 0.47772 C0.00621 0.47537 -0.01056 0.49792 -0.02443 0.48738 C-0.03830 0.47683 -0.06114 0.43425 -0.05878 0.41445 C-0.05643 0.39464 -0.02419 0.38473 -0.01032 0.36855 C0.00354 0.35236 0.01025 0.32156 0.02443 0.31733 C0.03861 0.31310 0.06707 0.32697 0.07474 0.34316 C0.08241 0.35935 0.06877 0.38806 0.07046 0.41445 Z"
                />
              </clipPath>
              <clipPath id="canopy-clip-accent" clipPathUnits="objectBoundingBox">
                <path
                  d="M0.15109 0.40000 C0.15135 0.41083 0.14360 0.42421 0.13855 0.43360 C0.13351 0.44299 0.12818 0.45191 0.12084 0.45634 C0.11351 0.46077 0.10167 0.46576 0.09457 0.46016 C0.08746 0.45457 0.07964 0.43576 0.07823 0.42275 C0.07681 0.40974 0.08347 0.39624 0.08609 0.38211 C0.08871 0.36798 0.08791 0.34665 0.09394 0.33799 C0.09997 0.32933 0.11507 0.32505 0.12224 0.33015 C0.12942 0.33525 0.13220 0.35697 0.13701 0.36861 C0.14181 0.38025 0.15083 0.38917 0.15109 0.40000 Z M0.16684 0.18500 C0.16630 0.19731 0.16382 0.21222 0.15928 0.21964 C0.15475 0.22706 0.14650 0.22692 0.13962 0.22950 C0.13273 0.23208 0.12310 0.23985 0.11798 0.23513 C0.11285 0.23041 0.11042 0.21228 0.10884 0.20119 C0.10726 0.19010 0.10688 0.17937 0.10850 0.16860 C0.11012 0.15784 0.11333 0.14169 0.11856 0.13660 C0.12379 0.13150 0.13255 0.13652 0.13987 0.13805 C0.14719 0.13958 0.15801 0.13795 0.16250 0.14577 C0.16700 0.15360 0.16738 0.17269 0.16684 0.18500 Z"
                />
              </clipPath>
              <clipPath id="canopy-clip-bg-mobile" clipPathUnits="objectBoundingBox">
                <path
                  d="M0.38717 0.10120 C0.38818 0.11627 0.37319 0.13272 0.35391 0.14765 C0.33463 0.16258 0.30735 0.18467 0.27148 0.19077 C0.23560 0.19687 0.17280 0.19418 0.13866 0.18426 C0.10452 0.17434 0.07515 0.14947 0.06664 0.13123 C0.05813 0.11299 0.07028 0.08926 0.08758 0.07482 C0.10488 0.06037 0.13947 0.05594 0.17044 0.04455 C0.20140 0.03316 0.24380 0.00438 0.27336 0.00649 C0.30293 0.00859 0.32887 0.04140 0.34784 0.05719 C0.36680 0.07297 0.38616 0.08612 0.38717 0.10120 Z M0.88129 0.11710 C0.88506 0.13067 0.85456 0.15279 0.83096 0.16237 C0.80736 0.17196 0.77091 0.17176 0.73968 0.17461 C0.70845 0.17745 0.66746 0.18547 0.64356 0.17945 C0.61967 0.17342 0.60713 0.15293 0.59630 0.13846 C0.58548 0.12399 0.57314 0.10860 0.57861 0.09265 C0.58408 0.07670 0.60148 0.05044 0.62913 0.04275 C0.65677 0.03507 0.71461 0.04017 0.74448 0.04654 C0.77434 0.05290 0.78553 0.06918 0.80833 0.08094 C0.83113 0.09270 0.87752 0.10353 0.88129 0.11710 Z M0.64540 0.22586 C0.64520 0.24273 0.63236 0.26666 0.60896 0.27623 C0.58556 0.28580 0.53997 0.27992 0.50499 0.28328 C0.47001 0.28663 0.42857 0.30140 0.39909 0.29637 C0.36961 0.29133 0.33990 0.26936 0.32812 0.25308 C0.31633 0.23680 0.31556 0.21416 0.32837 0.19869 C0.34118 0.18322 0.37400 0.16945 0.40497 0.16024 C0.43594 0.15103 0.47999 0.14096 0.51418 0.14342 C0.54838 0.14588 0.58827 0.16127 0.61014 0.17501 C0.63201 0.18875 0.64560 0.20899 0.64540 0.22586 Z M0.25092 0.32077 C0.24904 0.33623 0.24050 0.34979 0.22022 0.36444 C0.19995 0.37909 0.16032 0.40834 0.12925 0.40870 C0.09819 0.40905 0.05441 0.38123 0.03383 0.36658 C0.01324 0.35193 0.00459 0.33549 0.00574 0.32077 C0.00689 0.30606 0.02013 0.29055 0.04071 0.27827 C0.06130 0.26600 0.09746 0.24820 0.12925 0.24711 C0.16105 0.24601 0.21123 0.25942 0.23151 0.27169 C0.25178 0.28397 0.25280 0.30532 0.25092 0.32077 Z M1.00675 0.29792 C1.00288 0.31501 0.96532 0.33098 0.93800 0.34359 C0.91069 0.35620 0.87407 0.37380 0.84286 0.37356 C0.81165 0.37332 0.77327 0.35475 0.75074 0.34214 C0.72820 0.32954 0.71196 0.31473 0.70765 0.29792 C0.70334 0.28111 0.70232 0.25180 0.72486 0.24128 C0.74739 0.23076 0.80346 0.23484 0.84286 0.23481 C0.88226 0.23478 0.93393 0.23058 0.96125 0.24110 C0.98856 0.25162 1.01063 0.28084 1.00675 0.29792 Z M0.54379 0.40663 C0.53876 0.42010 0.49614 0.43067 0.47279 0.43980 C0.44944 0.44894 0.42947 0.46013 0.40369 0.46144 C0.37792 0.46276 0.33711 0.45683 0.31814 0.44770 C0.29917 0.43856 0.29342 0.42201 0.28990 0.40663 C0.28638 0.39126 0.27806 0.36638 0.29703 0.35544 C0.31600 0.34449 0.36937 0.34040 0.40369 0.34099 C0.43802 0.34158 0.47962 0.34804 0.50297 0.35898 C0.52632 0.36992 0.54882 0.39316 0.54379 0.40663 Z M0.81167 0.45484 C0.81514 0.46951 0.81042 0.49361 0.79039 0.50304 C0.77036 0.51248 0.72340 0.50954 0.69150 0.51145 C0.65960 0.51335 0.62338 0.52025 0.59897 0.51448 C0.57456 0.50870 0.55447 0.49048 0.54502 0.47680 C0.53558 0.46312 0.53063 0.44379 0.54229 0.43240 C0.55395 0.42102 0.58962 0.41553 0.61498 0.40851 C0.64033 0.40148 0.66867 0.38918 0.69442 0.39027 C0.72018 0.39136 0.74998 0.40428 0.76952 0.41504 C0.78906 0.42580 0.80819 0.44017 0.81167 0.45484 Z M0.31508 0.54182 C0.31577 0.55673 0.32678 0.57811 0.31007 0.58755 C0.29335 0.59700 0.24731 0.59813 0.21479 0.59850 C0.18227 0.59887 0.13581 0.59920 0.11493 0.58975 C0.09405 0.58030 0.08459 0.55543 0.08951 0.54182 C0.09444 0.52821 0.12362 0.51999 0.14450 0.50808 C0.16537 0.49617 0.18789 0.47201 0.21479 0.47035 C0.24169 0.46868 0.28918 0.48617 0.30590 0.49809 C0.32261 0.51000 0.31438 0.52691 0.31508 0.54182 Z"
                />
              </clipPath>
              <mask id="canopy-mask-bg-4" maskUnits="objectBoundingBox">
                  <path d="M0.94637 0.40717 C0.94425 0.43293 0.94154 0.45862 0.93084 0.47253 C0.92014 0.48645 0.89929 0.49444 0.88219 0.49065 C0.86509 0.48687 0.83942 0.47327 0.82825 0.44981 C0.81707 0.42635 0.80633 0.37159 0.81513 0.34990 C0.82393 0.32820 0.85964 0.32496 0.88105 0.31964 C0.90246 0.31431 0.93269 0.30337 0.94357 0.31796 C0.95446 0.33255 0.94850 0.38141 0.94637 0.40717 Z" fill="none" stroke="#fff" strokeWidth="0.006" />
                  <path d="M0.80035 0.47130 C0.80223 0.49312 0.80756 0.52836 0.79707 0.54457 C0.78658 0.56079 0.75233 0.57540 0.73743 0.56858 C0.72252 0.56177 0.71525 0.52642 0.70765 0.50369 C0.70005 0.48097 0.68600 0.44810 0.69183 0.43225 C0.69767 0.41640 0.72702 0.41170 0.74268 0.40860 C0.75834 0.40549 0.77618 0.40318 0.78579 0.41363 C0.79541 0.42408 0.79847 0.44947 0.80035 0.47130 Z" fill="#000" stroke="#000" strokeWidth="0.01" />
                </mask>
              <mask id="canopy-mask-bg-16" maskUnits="objectBoundingBox">
                  <path d="M0.59116 0.16215 C0.59468 0.19146 0.57214 0.26287 0.55391 0.27345 C0.53567 0.28402 0.49792 0.24413 0.48175 0.22558 C0.46559 0.20703 0.45736 0.18386 0.45691 0.16215 C0.45647 0.14045 0.46642 0.10612 0.47906 0.09537 C0.49170 0.08461 0.51409 0.08648 0.53277 0.09761 C0.55146 0.10875 0.58763 0.13285 0.59116 0.16215 Z" fill="none" stroke="#fff" strokeWidth="0.006" />
                  <path d="M0.79133 0.12855 C0.79054 0.17126 0.77711 0.22312 0.75583 0.24712 C0.73456 0.27111 0.69227 0.27978 0.66370 0.27250 C0.63512 0.26522 0.59529 0.23774 0.58439 0.20345 C0.57348 0.16916 0.58345 0.09523 0.59825 0.06676 C0.61304 0.03828 0.64610 0.04524 0.67316 0.03258 C0.70021 0.01993 0.74088 -0.02517 0.76057 -0.00917 C0.78027 0.00682 0.79212 0.08583 0.79133 0.12855 Z" fill="#000" stroke="#000" strokeWidth="0.01" />
                </mask>
              <mask id="canopy-mask-mobile-0" maskUnits="objectBoundingBox">
                  <path d="M0.38717 0.10120 C0.38818 0.11627 0.37319 0.13272 0.35391 0.14765 C0.33463 0.16258 0.30735 0.18467 0.27148 0.19077 C0.23560 0.19687 0.17280 0.19418 0.13866 0.18426 C0.10452 0.17434 0.07515 0.14947 0.06664 0.13123 C0.05813 0.11299 0.07028 0.08926 0.08758 0.07482 C0.10488 0.06037 0.13947 0.05594 0.17044 0.04455 C0.20140 0.03316 0.24380 0.00438 0.27336 0.00649 C0.30293 0.00859 0.32887 0.04140 0.34784 0.05719 C0.36680 0.07297 0.38616 0.08612 0.38717 0.10120 Z" fill="none" stroke="#fff" strokeWidth="0.006" />
                  <path d="M0.64540 0.22586 C0.64520 0.24273 0.63236 0.26666 0.60896 0.27623 C0.58556 0.28580 0.53997 0.27992 0.50499 0.28328 C0.47001 0.28663 0.42857 0.30140 0.39909 0.29637 C0.36961 0.29133 0.33990 0.26936 0.32812 0.25308 C0.31633 0.23680 0.31556 0.21416 0.32837 0.19869 C0.34118 0.18322 0.37400 0.16945 0.40497 0.16024 C0.43594 0.15103 0.47999 0.14096 0.51418 0.14342 C0.54838 0.14588 0.58827 0.16127 0.61014 0.17501 C0.63201 0.18875 0.64560 0.20899 0.64540 0.22586 Z" fill="#000" stroke="#000" strokeWidth="0.01" />
                </mask>
              <mask id="canopy-mask-mobile-1" maskUnits="objectBoundingBox">
                  <path d="M0.88129 0.11710 C0.88506 0.13067 0.85456 0.15279 0.83096 0.16237 C0.80736 0.17196 0.77091 0.17176 0.73968 0.17461 C0.70845 0.17745 0.66746 0.18547 0.64356 0.17945 C0.61967 0.17342 0.60713 0.15293 0.59630 0.13846 C0.58548 0.12399 0.57314 0.10860 0.57861 0.09265 C0.58408 0.07670 0.60148 0.05044 0.62913 0.04275 C0.65677 0.03507 0.71461 0.04017 0.74448 0.04654 C0.77434 0.05290 0.78553 0.06918 0.80833 0.08094 C0.83113 0.09270 0.87752 0.10353 0.88129 0.11710 Z" fill="none" stroke="#fff" strokeWidth="0.006" />
                  <path d="M0.64540 0.22586 C0.64520 0.24273 0.63236 0.26666 0.60896 0.27623 C0.58556 0.28580 0.53997 0.27992 0.50499 0.28328 C0.47001 0.28663 0.42857 0.30140 0.39909 0.29637 C0.36961 0.29133 0.33990 0.26936 0.32812 0.25308 C0.31633 0.23680 0.31556 0.21416 0.32837 0.19869 C0.34118 0.18322 0.37400 0.16945 0.40497 0.16024 C0.43594 0.15103 0.47999 0.14096 0.51418 0.14342 C0.54838 0.14588 0.58827 0.16127 0.61014 0.17501 C0.63201 0.18875 0.64560 0.20899 0.64540 0.22586 Z" fill="#000" stroke="#000" strokeWidth="0.01" />
                </mask>
              <mask id="canopy-mask-mobile-5" maskUnits="objectBoundingBox">
                  <path d="M0.54379 0.40663 C0.53876 0.42010 0.49614 0.43067 0.47279 0.43980 C0.44944 0.44894 0.42947 0.46013 0.40369 0.46144 C0.37792 0.46276 0.33711 0.45683 0.31814 0.44770 C0.29917 0.43856 0.29342 0.42201 0.28990 0.40663 C0.28638 0.39126 0.27806 0.36638 0.29703 0.35544 C0.31600 0.34449 0.36937 0.34040 0.40369 0.34099 C0.43802 0.34158 0.47962 0.34804 0.50297 0.35898 C0.52632 0.36992 0.54882 0.39316 0.54379 0.40663 Z" fill="none" stroke="#fff" strokeWidth="0.006" />
                  <path d="M0.81167 0.45484 C0.81514 0.46951 0.81042 0.49361 0.79039 0.50304 C0.77036 0.51248 0.72340 0.50954 0.69150 0.51145 C0.65960 0.51335 0.62338 0.52025 0.59897 0.51448 C0.57456 0.50870 0.55447 0.49048 0.54502 0.47680 C0.53558 0.46312 0.53063 0.44379 0.54229 0.43240 C0.55395 0.42102 0.58962 0.41553 0.61498 0.40851 C0.64033 0.40148 0.66867 0.38918 0.69442 0.39027 C0.72018 0.39136 0.74998 0.40428 0.76952 0.41504 C0.78906 0.42580 0.80819 0.44017 0.81167 0.45484 Z" fill="#000" stroke="#000" strokeWidth="0.01" />
                </mask>
            </defs>
          </svg>

          <div className="hero-canopy" aria-hidden="true" />
          <svg
            className="canopy-outline"
            viewBox="0 0 1 1"
            preserveAspectRatio="none"
            fill="none"
            aria-hidden="true"
          >
            
            <path vectorEffect="non-scaling-stroke" d="M0.44229 0.39020 C0.44146 0.41093 0.42341 0.43541 0.41279 0.45184 C0.40216 0.46827 0.39273 0.47929 0.37854 0.48878 C0.36435 0.49826 0.34486 0.51150 0.32765 0.50875 C0.31043 0.50599 0.28329 0.49200 0.27523 0.47224 C0.26717 0.45248 0.27827 0.41604 0.27930 0.39020 C0.28032 0.36436 0.27279 0.33551 0.28139 0.31720 C0.28999 0.29890 0.31442 0.28732 0.33088 0.28036 C0.34734 0.27340 0.36567 0.26758 0.38015 0.27543 C0.39463 0.28328 0.40739 0.30831 0.41775 0.32744 C0.42811 0.34657 0.44312 0.36946 0.44229 0.39020 Z" />
            <path vectorEffect="non-scaling-stroke" d="M0.30751 0.10171 C0.30758 0.11891 0.29710 0.14268 0.28698 0.15431 C0.27686 0.16595 0.26014 0.17410 0.24681 0.17151 C0.23348 0.16892 0.21081 0.15446 0.20699 0.13880 C0.20317 0.12313 0.21699 0.09364 0.22389 0.07752 C0.23079 0.06139 0.23794 0.04646 0.24838 0.04206 C0.25882 0.03767 0.27667 0.04119 0.28653 0.05113 C0.29638 0.06107 0.30743 0.08452 0.30751 0.10171 Z" />
            <path vectorEffect="non-scaling-stroke" d="M0.95999 0.13565 C0.95790 0.14828 0.95698 0.15230 0.95246 0.16264 C0.94794 0.17298 0.94133 0.19462 0.93285 0.19771 C0.92437 0.20081 0.90545 0.19157 0.90158 0.18123 C0.89771 0.17088 0.90889 0.14993 0.90964 0.13565 C0.91039 0.12137 0.90223 0.10385 0.90610 0.09555 C0.90997 0.08725 0.92304 0.08728 0.93285 0.08583 C0.94267 0.08438 0.96047 0.07856 0.96500 0.08686 C0.96952 0.09516 0.96208 0.12302 0.95999 0.13565 Z" />
            <path vectorEffect="non-scaling-stroke" d="M0.34874 0.66349 C0.34977 0.69267 0.34065 0.75227 0.32655 0.76153 C0.31245 0.77080 0.28291 0.73543 0.26412 0.71909 C0.24532 0.70275 0.21546 0.68764 0.21378 0.66349 C0.21209 0.63935 0.23624 0.58705 0.25400 0.57421 C0.27177 0.56138 0.30457 0.57161 0.32035 0.58649 C0.33614 0.60137 0.34771 0.63432 0.34874 0.66349 Z" />
            <path vectorEffect="non-scaling-stroke" d="M0.94637 0.40717 C0.94425 0.43293 0.94154 0.45862 0.93084 0.47253 C0.92014 0.48645 0.89929 0.49444 0.88219 0.49065 C0.86509 0.48687 0.83942 0.47327 0.82825 0.44981 C0.81707 0.42635 0.80633 0.37159 0.81513 0.34990 C0.82393 0.32820 0.85964 0.32496 0.88105 0.31964 C0.90246 0.31431 0.93269 0.30337 0.94357 0.31796 C0.95446 0.33255 0.94850 0.38141 0.94637 0.40717 Z" mask="url(#canopy-mask-bg-4)" />
            <path vectorEffect="non-scaling-stroke" d="M0.72618 0.80215 C0.72474 0.82511 0.70410 0.85315 0.69010 0.86222 C0.67610 0.87129 0.65531 0.86660 0.64220 0.85659 C0.62909 0.84658 0.61274 0.82376 0.61142 0.80215 C0.61010 0.78054 0.61974 0.73989 0.63429 0.72693 C0.64885 0.71397 0.68344 0.71187 0.69875 0.72441 C0.71407 0.73694 0.72762 0.77918 0.72618 0.80215 Z" />
            <path vectorEffect="non-scaling-stroke" d="M1.04976 0.88111 C1.04992 0.90051 1.06140 0.93075 1.05487 0.94015 C1.04834 0.94955 1.02379 0.93110 1.01056 0.93748 C0.99733 0.94386 0.98698 0.97849 0.97550 0.97845 C0.96402 0.97842 0.94824 0.95349 0.94170 0.93727 C0.93516 0.92104 0.93207 0.89597 0.93623 0.88111 C0.94040 0.86624 0.96007 0.86476 0.96668 0.84811 C0.97329 0.83145 0.96879 0.78393 0.97588 0.78116 C0.98298 0.77840 0.99627 0.82443 1.00927 0.83153 C1.02227 0.83862 1.04714 0.81546 1.05389 0.82372 C1.06064 0.83198 1.04960 0.86170 1.04976 0.88111 Z" />
            <path vectorEffect="non-scaling-stroke" d="M0.49228 0.86075 C0.49048 0.87404 0.48748 0.88625 0.48181 0.89381 C0.47614 0.90137 0.46677 0.90300 0.45826 0.90610 C0.44976 0.90919 0.43985 0.91575 0.43078 0.91238 C0.42171 0.90902 0.40745 0.89816 0.40384 0.88593 C0.40023 0.87370 0.40444 0.85202 0.40910 0.83901 C0.41377 0.82600 0.42362 0.81277 0.43182 0.80786 C0.44002 0.80295 0.44817 0.80852 0.45829 0.80955 C0.46842 0.81058 0.48692 0.80551 0.49258 0.81404 C0.49825 0.82258 0.49407 0.84745 0.49228 0.86075 Z" />
            <path vectorEffect="non-scaling-stroke" d="M0.19621 0.40106 C0.19509 0.41266 0.17972 0.42461 0.17176 0.43066 C0.16379 0.43671 0.15705 0.43832 0.14844 0.43738 C0.13982 0.43644 0.12520 0.43513 0.12004 0.42503 C0.11489 0.41492 0.11297 0.38824 0.11752 0.37673 C0.12207 0.36522 0.13719 0.35856 0.14735 0.35595 C0.15750 0.35334 0.17031 0.35355 0.17845 0.36106 C0.18660 0.36858 0.19733 0.38946 0.19621 0.40106 Z" />
            <path vectorEffect="non-scaling-stroke" d="M0.59116 0.16215 C0.59468 0.19146 0.57214 0.26287 0.55391 0.27345 C0.53567 0.28402 0.49792 0.24413 0.48175 0.22558 C0.46559 0.20703 0.45736 0.18386 0.45691 0.16215 C0.45647 0.14045 0.46642 0.10612 0.47906 0.09537 C0.49170 0.08461 0.51409 0.08648 0.53277 0.09761 C0.55146 0.10875 0.58763 0.13285 0.59116 0.16215 Z" mask="url(#canopy-mask-bg-16)" />
            <path vectorEffect="non-scaling-stroke" d="M0.66561 0.57277 C0.66613 0.58380 0.66349 0.60184 0.65782 0.60858 C0.65215 0.61531 0.64220 0.61442 0.63162 0.61318 C0.62103 0.61195 0.59928 0.61224 0.59432 0.60117 C0.58936 0.59010 0.59573 0.55861 0.60184 0.54674 C0.60796 0.53488 0.62219 0.53071 0.63100 0.52999 C0.63982 0.52926 0.64896 0.53527 0.65473 0.54240 C0.66049 0.54953 0.66510 0.56174 0.66561 0.57277 Z" />
            <path vectorEffect="non-scaling-stroke" d="M0.30443 0.94838 C0.30405 0.96889 0.30057 0.99416 0.28849 1.00718 C0.27640 1.02020 0.24333 1.03628 0.23190 1.02647 C0.22048 1.01667 0.21787 0.96983 0.21993 0.94838 C0.22199 0.92692 0.23247 0.90844 0.24427 0.89773 C0.25608 0.88702 0.28072 0.87567 0.29075 0.88412 C0.30077 0.89256 0.30480 0.92786 0.30443 0.94838 Z" />
            <path vectorEffect="non-scaling-stroke" d="M0.07332 0.84893 C0.07204 0.86658 0.08315 0.88695 0.07840 0.89761 C0.07366 0.90827 0.05729 0.91199 0.04484 0.91287 C0.03238 0.91376 0.01207 0.91359 0.00368 0.90293 C-0.00471 0.89227 -0.00648 0.86678 -0.00549 0.84893 C-0.00450 0.83108 0.00124 0.80651 0.00963 0.79581 C0.01801 0.78511 0.03210 0.78542 0.04484 0.78474 C0.05757 0.78406 0.08131 0.78102 0.08606 0.79172 C0.09080 0.80242 0.07460 0.83128 0.07332 0.84893 Z" />
            <path vectorEffect="non-scaling-stroke" d="M0.16645 0.19405 C0.16646 0.20257 0.16006 0.21045 0.15581 0.21969 C0.15155 0.22892 0.14773 0.24613 0.14091 0.24944 C0.13409 0.25274 0.12099 0.24607 0.11488 0.23952 C0.10877 0.23297 0.10473 0.21966 0.10425 0.21015 C0.10378 0.20064 0.10926 0.19073 0.11203 0.18247 C0.11481 0.17420 0.11632 0.16608 0.12092 0.16054 C0.12552 0.15501 0.13383 0.14792 0.13962 0.14926 C0.14542 0.15059 0.15122 0.16108 0.15570 0.16854 C0.16017 0.17601 0.16643 0.18552 0.16645 0.19405 Z" />
            <path vectorEffect="non-scaling-stroke" d="M0.80035 0.47130 C0.80223 0.49312 0.80756 0.52836 0.79707 0.54457 C0.78658 0.56079 0.75233 0.57540 0.73743 0.56858 C0.72252 0.56177 0.71525 0.52642 0.70765 0.50369 C0.70005 0.48097 0.68600 0.44810 0.69183 0.43225 C0.69767 0.41640 0.72702 0.41170 0.74268 0.40860 C0.75834 0.40549 0.77618 0.40318 0.78579 0.41363 C0.79541 0.42408 0.79847 0.44947 0.80035 0.47130 Z" />
            <path vectorEffect="non-scaling-stroke" d="M0.20319 0.71326 C0.20262 0.73332 0.18701 0.75901 0.17386 0.77032 C0.16072 0.78164 0.13332 0.79066 0.12434 0.78115 C0.11536 0.77164 0.11862 0.73278 0.11997 0.71326 C0.12132 0.69375 0.12288 0.67461 0.13243 0.66406 C0.14198 0.65350 0.16548 0.64175 0.17727 0.64995 C0.18906 0.65815 0.20376 0.69320 0.20319 0.71326 Z" />
            <path vectorEffect="non-scaling-stroke" d="M0.79133 0.12855 C0.79054 0.17126 0.77711 0.22312 0.75583 0.24712 C0.73456 0.27111 0.69227 0.27978 0.66370 0.27250 C0.63512 0.26522 0.59529 0.23774 0.58439 0.20345 C0.57348 0.16916 0.58345 0.09523 0.59825 0.06676 C0.61304 0.03828 0.64610 0.04524 0.67316 0.03258 C0.70021 0.01993 0.74088 -0.02517 0.76057 -0.00917 C0.78027 0.00682 0.79212 0.08583 0.79133 0.12855 Z" />
            <path vectorEffect="non-scaling-stroke" d="M0.87138 0.99887 C0.87051 1.01442 0.85751 1.03253 0.84835 1.04067 C0.83918 1.04881 0.82744 1.04974 0.81637 1.04770 C0.80531 1.04566 0.78739 1.04137 0.78194 1.02844 C0.77649 1.01552 0.77775 0.98175 0.78367 0.97017 C0.78958 0.95858 0.80578 0.96273 0.81743 0.95893 C0.82909 0.95512 0.84462 0.94068 0.85361 0.94734 C0.86260 0.95399 0.87226 0.98331 0.87138 0.99887 Z" />
            <path vectorEffect="non-scaling-stroke" d="M0.65028 0.98579 C0.64962 0.99708 0.64151 1.00444 0.63609 1.01593 C0.63067 1.02743 0.62567 1.05125 0.61777 1.05476 C0.60988 1.05826 0.59673 1.04524 0.58873 1.03696 C0.58072 1.02868 0.57322 1.01736 0.56974 1.00509 C0.56626 0.99283 0.56362 0.97268 0.56785 0.96337 C0.57209 0.95406 0.58719 0.95381 0.59516 0.94924 C0.60312 0.94468 0.60817 0.93614 0.61565 0.93596 C0.62312 0.93579 0.63424 0.93988 0.64001 0.94819 C0.64578 0.95649 0.65093 0.97450 0.65028 0.98579 Z" />
            <path vectorEffect="non-scaling-stroke" d="M0.10786 0.08019 C0.10757 0.09919 0.09600 0.12887 0.08556 0.13696 C0.07512 0.14505 0.05809 0.13282 0.04523 0.12873 C0.03237 0.12464 0.01280 0.12434 0.00841 0.11241 C0.00402 0.10048 0.01317 0.07387 0.01891 0.05714 C0.02465 0.04042 0.03148 0.01777 0.04287 0.01208 C0.05427 0.00638 0.07644 0.01163 0.08727 0.02298 C0.09810 0.03433 0.10814 0.06120 0.10786 0.08019 Z" />
            <path vectorEffect="non-scaling-stroke" d="M0.60039 0.36372 C0.60072 0.37716 0.59765 0.39442 0.59018 0.40692 C0.58271 0.41942 0.56721 0.44034 0.55556 0.43871 C0.54392 0.43709 0.52680 0.41565 0.52031 0.39717 C0.51382 0.37869 0.51033 0.34218 0.51663 0.32784 C0.52293 0.31349 0.54620 0.31137 0.55813 0.31111 C0.57007 0.31084 0.58118 0.31746 0.58822 0.32622 C0.59526 0.33499 0.60007 0.35027 0.60039 0.36372 Z" />
            <path vectorEffect="non-scaling-stroke" d="M0.07046 0.41445 C0.07216 0.44083 0.09258 0.49093 0.08491 0.50148 C0.07724 0.51202 0.04265 0.48007 0.02443 0.47772 C0.00621 0.47537 -0.01056 0.49792 -0.02443 0.48738 C-0.03830 0.47683 -0.06114 0.43425 -0.05878 0.41445 C-0.05643 0.39464 -0.02419 0.38473 -0.01032 0.36855 C0.00354 0.35236 0.01025 0.32156 0.02443 0.31733 C0.03861 0.31310 0.06707 0.32697 0.07474 0.34316 C0.08241 0.35935 0.06877 0.38806 0.07046 0.41445 Z" />
            </svg>

          <svg
            className="canopy-outline canopy-outline-mobile"
            viewBox="0 0 1 1"
            preserveAspectRatio="none"
            fill="none"
            aria-hidden="true"
          >
            
            <path vectorEffect="non-scaling-stroke" d="M0.38717 0.10120 C0.38818 0.11627 0.37319 0.13272 0.35391 0.14765 C0.33463 0.16258 0.30735 0.18467 0.27148 0.19077 C0.23560 0.19687 0.17280 0.19418 0.13866 0.18426 C0.10452 0.17434 0.07515 0.14947 0.06664 0.13123 C0.05813 0.11299 0.07028 0.08926 0.08758 0.07482 C0.10488 0.06037 0.13947 0.05594 0.17044 0.04455 C0.20140 0.03316 0.24380 0.00438 0.27336 0.00649 C0.30293 0.00859 0.32887 0.04140 0.34784 0.05719 C0.36680 0.07297 0.38616 0.08612 0.38717 0.10120 Z" mask="url(#canopy-mask-mobile-0)" />
            <path vectorEffect="non-scaling-stroke" d="M0.88129 0.11710 C0.88506 0.13067 0.85456 0.15279 0.83096 0.16237 C0.80736 0.17196 0.77091 0.17176 0.73968 0.17461 C0.70845 0.17745 0.66746 0.18547 0.64356 0.17945 C0.61967 0.17342 0.60713 0.15293 0.59630 0.13846 C0.58548 0.12399 0.57314 0.10860 0.57861 0.09265 C0.58408 0.07670 0.60148 0.05044 0.62913 0.04275 C0.65677 0.03507 0.71461 0.04017 0.74448 0.04654 C0.77434 0.05290 0.78553 0.06918 0.80833 0.08094 C0.83113 0.09270 0.87752 0.10353 0.88129 0.11710 Z" mask="url(#canopy-mask-mobile-1)" />
            <path vectorEffect="non-scaling-stroke" d="M0.64540 0.22586 C0.64520 0.24273 0.63236 0.26666 0.60896 0.27623 C0.58556 0.28580 0.53997 0.27992 0.50499 0.28328 C0.47001 0.28663 0.42857 0.30140 0.39909 0.29637 C0.36961 0.29133 0.33990 0.26936 0.32812 0.25308 C0.31633 0.23680 0.31556 0.21416 0.32837 0.19869 C0.34118 0.18322 0.37400 0.16945 0.40497 0.16024 C0.43594 0.15103 0.47999 0.14096 0.51418 0.14342 C0.54838 0.14588 0.58827 0.16127 0.61014 0.17501 C0.63201 0.18875 0.64560 0.20899 0.64540 0.22586 Z" />
            <path vectorEffect="non-scaling-stroke" d="M0.25092 0.32077 C0.24904 0.33623 0.24050 0.34979 0.22022 0.36444 C0.19995 0.37909 0.16032 0.40834 0.12925 0.40870 C0.09819 0.40905 0.05441 0.38123 0.03383 0.36658 C0.01324 0.35193 0.00459 0.33549 0.00574 0.32077 C0.00689 0.30606 0.02013 0.29055 0.04071 0.27827 C0.06130 0.26600 0.09746 0.24820 0.12925 0.24711 C0.16105 0.24601 0.21123 0.25942 0.23151 0.27169 C0.25178 0.28397 0.25280 0.30532 0.25092 0.32077 Z" />
            <path vectorEffect="non-scaling-stroke" d="M1.00675 0.29792 C1.00288 0.31501 0.96532 0.33098 0.93800 0.34359 C0.91069 0.35620 0.87407 0.37380 0.84286 0.37356 C0.81165 0.37332 0.77327 0.35475 0.75074 0.34214 C0.72820 0.32954 0.71196 0.31473 0.70765 0.29792 C0.70334 0.28111 0.70232 0.25180 0.72486 0.24128 C0.74739 0.23076 0.80346 0.23484 0.84286 0.23481 C0.88226 0.23478 0.93393 0.23058 0.96125 0.24110 C0.98856 0.25162 1.01063 0.28084 1.00675 0.29792 Z" />
            <path vectorEffect="non-scaling-stroke" d="M0.54379 0.40663 C0.53876 0.42010 0.49614 0.43067 0.47279 0.43980 C0.44944 0.44894 0.42947 0.46013 0.40369 0.46144 C0.37792 0.46276 0.33711 0.45683 0.31814 0.44770 C0.29917 0.43856 0.29342 0.42201 0.28990 0.40663 C0.28638 0.39126 0.27806 0.36638 0.29703 0.35544 C0.31600 0.34449 0.36937 0.34040 0.40369 0.34099 C0.43802 0.34158 0.47962 0.34804 0.50297 0.35898 C0.52632 0.36992 0.54882 0.39316 0.54379 0.40663 Z" mask="url(#canopy-mask-mobile-5)" />
            <path vectorEffect="non-scaling-stroke" d="M0.81167 0.45484 C0.81514 0.46951 0.81042 0.49361 0.79039 0.50304 C0.77036 0.51248 0.72340 0.50954 0.69150 0.51145 C0.65960 0.51335 0.62338 0.52025 0.59897 0.51448 C0.57456 0.50870 0.55447 0.49048 0.54502 0.47680 C0.53558 0.46312 0.53063 0.44379 0.54229 0.43240 C0.55395 0.42102 0.58962 0.41553 0.61498 0.40851 C0.64033 0.40148 0.66867 0.38918 0.69442 0.39027 C0.72018 0.39136 0.74998 0.40428 0.76952 0.41504 C0.78906 0.42580 0.80819 0.44017 0.81167 0.45484 Z" />
            <path vectorEffect="non-scaling-stroke" d="M0.31508 0.54182 C0.31577 0.55673 0.32678 0.57811 0.31007 0.58755 C0.29335 0.59700 0.24731 0.59813 0.21479 0.59850 C0.18227 0.59887 0.13581 0.59920 0.11493 0.58975 C0.09405 0.58030 0.08459 0.55543 0.08951 0.54182 C0.09444 0.52821 0.12362 0.51999 0.14450 0.50808 C0.16537 0.49617 0.18789 0.47201 0.21479 0.47035 C0.24169 0.46868 0.28918 0.48617 0.30590 0.49809 C0.32261 0.51000 0.31438 0.52691 0.31508 0.54182 Z" />
            </svg>

          <div className="hero-lattice" aria-hidden="true">
            <svg className="lattice" viewBox="0 0 800 900" fill="none">
              <g className="lattice-grid" stroke="currentColor" strokeWidth="1">
                <path className="lattice-line" d="M80 120 H720" />
                <path className="lattice-line" d="M80 280 H720" />
                <path className="lattice-line" d="M80 440 H720" />
                <path className="lattice-line" d="M80 600 H720" />
                <path className="lattice-line" d="M80 760 H720" />
                <path className="lattice-line" d="M240 60 V840" />
                <path className="lattice-line" d="M400 60 V840" />
                <path className="lattice-line" d="M560 60 V840" />
              </g>
              <g className="lattice-paths" stroke="currentColor" strokeWidth="1.5">
                <path
                  className="lattice-line"
                  d="M240 440 C265 428 300 451 330 440 S368 449 400 440"
                />
                <path className="lattice-line signal-path" d="M400 440 C420 400 380 330 400 280" />
              </g>
              <g className="lattice-nodes" fill="currentColor">
                <circle className="lattice-node" cx="240" cy="440" r="4" />
                <circle className="lattice-node signal-node" cx="400" cy="440" r="5" />
                <circle className="lattice-node" cx="400" cy="280" r="4" />
              </g>
            </svg>
          </div>

          <div className="hero-inner">
            <div className="hero-canopy-accent" aria-hidden="true" />
            <svg
              className="canopy-outline canopy-outline-accent"
              viewBox="0 0 1 1"
              preserveAspectRatio="none"
              fill="none"
              aria-hidden="true"
            >
              
            <path vectorEffect="non-scaling-stroke" d="M0.15109 0.40000 C0.15135 0.41083 0.14360 0.42421 0.13855 0.43360 C0.13351 0.44299 0.12818 0.45191 0.12084 0.45634 C0.11351 0.46077 0.10167 0.46576 0.09457 0.46016 C0.08746 0.45457 0.07964 0.43576 0.07823 0.42275 C0.07681 0.40974 0.08347 0.39624 0.08609 0.38211 C0.08871 0.36798 0.08791 0.34665 0.09394 0.33799 C0.09997 0.32933 0.11507 0.32505 0.12224 0.33015 C0.12942 0.33525 0.13220 0.35697 0.13701 0.36861 C0.14181 0.38025 0.15083 0.38917 0.15109 0.40000 Z" />
            <path vectorEffect="non-scaling-stroke" d="M0.16684 0.18500 C0.16630 0.19731 0.16382 0.21222 0.15928 0.21964 C0.15475 0.22706 0.14650 0.22692 0.13962 0.22950 C0.13273 0.23208 0.12310 0.23985 0.11798 0.23513 C0.11285 0.23041 0.11042 0.21228 0.10884 0.20119 C0.10726 0.19010 0.10688 0.17937 0.10850 0.16860 C0.11012 0.15784 0.11333 0.14169 0.11856 0.13660 C0.12379 0.13150 0.13255 0.13652 0.13987 0.13805 C0.14719 0.13958 0.15801 0.13795 0.16250 0.14577 C0.16700 0.15360 0.16738 0.17269 0.16684 0.18500 Z" />
            </svg>

            <h1 className="hero-brand">
              <span className="reveal-line">
                <span>kinomi</span>
              </span>
              <span className="reveal-line">
                <span>
                  <span className="hero-brand-dot">.</span>
                  <span className="signal">tech</span>
                </span>
              </span>
            </h1>

            <div className="hero-main">
              <div className="hero-copy">
                <p className="hero-kicker">Consulting and engineering.</p>
                <h2 className="hero-headline">
                  High-performance
                  <br className="br br-always" />
                  AI systems.
                </h2>
                <p className="hero-lede">
                  Specialized engineering that embeds AI into your business and improves
                  existing genAI pipelines.
                </p>
                <div className="hero-cta">
                  <a
                    className="btn btn-primary magnetic"
                    href={BOOK_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Book a 15m intro call
                  </a>
                  <button
                    type="button"
                    className="btn btn-ghost magnetic"
                    onClick={() => scrollToSection("expertise")}
                  >
                    See expertise
                  </button>
                </div>
              </div>

              {/* Temporarily hidden
              <aside className="appstore-callout" role="status" aria-label="Coming soon">
                <div className="appstore-callout-glass" aria-hidden="true" />
                <div className="appstore-callout-icon" aria-hidden="true">
                  <svg viewBox="0 0 32 32" fill="none">
                    <rect x="8.5" y="4.5" width="15" height="23" rx="3.5" stroke="currentColor" strokeWidth="1.6" />
                    <path d="M13 7.25h6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                    <circle cx="16" cy="23.5" r="1.35" fill="currentColor" />
                  </svg>
                </div>
                <div className="appstore-callout-copy">
                  <span className="appstore-callout-eyebrow">Coming Soon</span>
                  <span className="appstore-callout-title">Expanding to iOS development soon</span>
                </div>
              </aside>
              */}
            </div>
          </div>

          <button
            type="button"
            className="hero-scroll"
            onClick={() => scrollToSection("expertise")}
            aria-label="Scroll to next section"
          >
            <svg className="hero-scroll-arrow" viewBox="0 0 24 40" fill="none">
              <path d="M12 2v28" stroke="currentColor" strokeWidth="1.5" />
              <path d="M5 23l7 9 7-9" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
            </svg>
          </button>
        </header>

        <section id="expertise" className="section expertise">
          <div className="section-rail">
            <div className="expertise-head">
              <p className="section-label">Expertise</p>
              <h2 className="section-title">
                Capabilities
                <br className="br br-always" />
                with teeth.
              </h2>
              <p className="section-lead">
                From infrastructure to prompting – systems that behave predictably under real load,
                not demo conditions.
              </p>
            </div>

            <ol className="expertise-list">
              {expertise.map((item, index) => (
                <li className="expertise-item" key={item.title}>
                  <span className="expertise-index">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <div className="expertise-body">
                    <h3>{item.title}</h3>
                    <p>{item.body}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section id="approach" className="approach">
          <div className="approach-pin">
            <div className="section-canopy-layer" aria-hidden="true">
              <div className="section-canopy approach-canopy-fill" />
              <svg
                className="canopy-outline"
                viewBox="0 0 1 1"
                preserveAspectRatio="none"
                fill="none"
                aria-hidden="true"
              >
                <path vectorEffect="non-scaling-stroke" d="M0.69912 0.11331 C0.70139 0.13108 0.70117 0.15722 0.69155 0.17277 C0.68193 0.18833 0.65849 0.20145 0.64139 0.20663 C0.62430 0.21182 0.60546 0.21176 0.58899 0.20389 C0.57251 0.19602 0.55058 0.17441 0.54254 0.15942 C0.53449 0.14442 0.54292 0.12921 0.54071 0.11392 C0.53851 0.09863 0.52111 0.08293 0.52929 0.06769 C0.53747 0.05244 0.57251 0.02737 0.58978 0.02246 C0.60706 0.01754 0.61825 0.03091 0.63294 0.03819 C0.64762 0.04547 0.66687 0.05362 0.67790 0.06614 C0.68893 0.07866 0.69684 0.09554 0.69912 0.11331 Z" />
                <path vectorEffect="non-scaling-stroke" d="M0.35211 0.25458 C0.35068 0.26262 0.34021 0.27499 0.33385 0.28103 C0.32750 0.28706 0.32265 0.28700 0.31400 0.29078 C0.30534 0.29456 0.29073 0.30451 0.28194 0.30370 C0.27315 0.30288 0.26532 0.29364 0.26128 0.28589 C0.25723 0.27815 0.25563 0.26641 0.25767 0.25725 C0.25972 0.24809 0.26788 0.23678 0.27354 0.23095 C0.27919 0.22511 0.28367 0.22649 0.29160 0.22224 C0.29952 0.21799 0.31263 0.20367 0.32111 0.20543 C0.32959 0.20720 0.33731 0.22461 0.34248 0.23280 C0.34764 0.24099 0.35355 0.24654 0.35211 0.25458 Z" />
                <path vectorEffect="non-scaling-stroke" d="M0.76053 0.64859 C0.76095 0.66135 0.75812 0.68015 0.75199 0.69008 C0.74585 0.70000 0.73570 0.70124 0.72372 0.70814 C0.71174 0.71504 0.69247 0.73400 0.68010 0.73149 C0.66772 0.72898 0.65544 0.70693 0.64949 0.69307 C0.64354 0.67922 0.64482 0.66146 0.64442 0.64836 C0.64402 0.63526 0.64076 0.62467 0.64711 0.61446 C0.65346 0.60425 0.66973 0.59113 0.68250 0.58710 C0.69526 0.58306 0.71255 0.58583 0.72370 0.59024 C0.73486 0.59464 0.74332 0.60380 0.74945 0.61353 C0.75559 0.62325 0.76011 0.63583 0.76053 0.64859 Z" />
                <path vectorEffect="non-scaling-stroke" d="M0.88652 0.13082 C0.88681 0.13728 0.89206 0.14345 0.88822 0.15015 C0.88438 0.15686 0.87162 0.16833 0.86347 0.17107 C0.85531 0.17380 0.84610 0.17092 0.83929 0.16657 C0.83249 0.16223 0.82562 0.15120 0.82264 0.14498 C0.81965 0.13877 0.82180 0.13656 0.82137 0.12929 C0.82094 0.12202 0.81759 0.10892 0.82006 0.10137 C0.82253 0.09381 0.82770 0.08552 0.83621 0.08395 C0.84473 0.08239 0.86277 0.08743 0.87114 0.09200 C0.87951 0.09658 0.88387 0.10493 0.88643 0.11141 C0.88900 0.11788 0.88622 0.12437 0.88652 0.13082 Z" />
                <path vectorEffect="non-scaling-stroke" d="M0.33948 0.49919 C0.33725 0.51568 0.32961 0.53369 0.31986 0.54620 C0.31012 0.55872 0.29400 0.57073 0.28102 0.57429 C0.26804 0.57785 0.25199 0.57396 0.24196 0.56756 C0.23193 0.56117 0.22937 0.54559 0.22085 0.53592 C0.21234 0.52624 0.19322 0.52225 0.19086 0.50951 C0.18851 0.49677 0.19711 0.46838 0.20672 0.45946 C0.21633 0.45055 0.23417 0.45816 0.24853 0.45604 C0.26288 0.45392 0.27873 0.44822 0.29285 0.44676 C0.30697 0.44530 0.32549 0.43853 0.33326 0.44727 C0.34103 0.45601 0.34171 0.48271 0.33948 0.49919 Z" />
              </svg>
            </div>
            <div className="section-rail approach-layout">
              <div className="approach-sticky-copy">
                <p className="section-label">Approach</p>
                <h2 className="approach-thesis">
                  From glue to
                  <br className="br br-always" />
                  <em>correct</em> systems.
                </h2>
                <p className="approach-intro">
                  Most AI integrations stop at “it works.” We treat that as the starting line.
                  </p>
                </div>

              <div className="approach-beats" aria-live="polite">
                {approachBeats.map((beat) => (
                  <article className="approach-beat" key={beat.index}>
                    <span className="approach-beat-index">{beat.index}</span>
                    <h3>{beat.title}</h3>
                    <p>{beat.body}</p>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section id="testimonials" className="section testimonials">
          <div className="section-rail testimonials-main">
            <p className="section-label">Testimonials</p>
            {testimonials.map((item) => (
              <figure className="testimonial" key={item.name}>
                <blockquote className="testimonial-quote">
                  <p>{item.quote}</p>
                </blockquote>
                <figcaption className="testimonial-person">
                  <a
                    className="testimonial-person-link"
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`${item.name} on X, @${item.handle}`}
                  >
                    <span className="testimonial-avatar">
                      <img
                        className="testimonial-photo"
                        src={item.photo}
                        alt=""
                        width={item.photoWidth}
                        height={item.photoHeight}
                        decoding="async"
                      />
                      <span className="testimonial-x-badge">
                        <XLogo />
                      </span>
                    </span>
                    <div className="testimonial-meta">
                      <cite className="testimonial-name">
                        <XLogo className="testimonial-name-mark" />
                        {item.name}
                      </cite>
                      <span className="testimonial-handle">@{item.handle}</span>
                    </div>
                  </a>
                </figcaption>
              </figure>
            ))}
          </div>
        </section>

        <section id="founder" className="section founder">
          <div className="section-rail founder-main">
            <p className="section-label">About the founder</p>
            <div className="founder-layout">
              <img
                className="founder-photo"
                src="/alex_pic_cropped.jpeg"
                alt="Alex Yelisieiev"
                width={1053}
                height={1446}
                decoding="async"
              />
              <div className="founder-copy">
                <h2 className="founder-title">
                  Hello, I’m Alex
                  <span className="founder-wave" aria-hidden="true">
                    👋
                  </span>
                </h2>
                <p className="founder-body">
                  Working as an AI Engineer, I started making my own products. The first
                  one was quite successful. Now, I’ve decided to start Kinomi where we
                  bring our experience to those who need it.
                </p>
                <p className="founder-body">
                  Have an idea to build or a project to improve?
                  <br />
                  Let’s{" "}
                  <a
                    className="founder-link"
                    href={CHAT_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    get in touch
                  </a>{" "}
                  and see what we can achieve together.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section id="contact" className="section contact">
          <div className="section-rail contact-main">
            <p className="section-label">Contact</p>
            <h2 className="section-title contact-title">
              Let’s build
              <br className="br br-sm" />
              <span className="br-space br-space-lg"> </span>
              the real
              <span className="br-space br-space-sm"> </span>
              <br className="br br-lg" />
              thing.
            </h2>

            <div className="contact-actions">
              <a
                className="contact-link"
                href={BOOK_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                Book a call <span>↗</span>
              </a>
              <div className="contact-secondary">
                <a href={CHAT_URL} target="_blank" rel="noopener noreferrer">
                  Or chat with us
                </a>
              </div>
            </div>
          </div>

          <footer className="section-rail site-footer">
            <span>© {new Date().getFullYear()} kinomi.tech</span>
            <span>High-performance AI systems</span>
          </footer>
        </section>
      </div>
    </div>
  );
};

export default App;
